import React, { useEffect, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import CommentThread from '../../../../shared/components/comments/CommentThread';
import { ConfirmActionDialog } from '../../../../shared/components/patterns';
import {
  createDocumentNote,
  deleteDocumentNote,
  getDocumentNotes,
} from '@/modules/documents/services';

export default function DocumentNotesPanel({
  documentId,
  autoFocus = false,
  open,
  loadNotes = getDocumentNotes,
  saveNote = createDocumentNote,
  removeNote = deleteDocumentNote,
}) {
  const { currentUser } = useSession();
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState('');
  const [noteDeleteRequest, setNoteDeleteRequest] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!open || !documentId) {
        setNotes([]);
        setDeletingNoteId('');
        setError('');
        return;
      }

      setIsLoading(true);
      setError('');
      const result = await loadNotes(documentId);
      if (cancelled) return;

      setIsLoading(false);
      if (result?.ok === false) {
        setNotes([]);
        setError(result.message || 'Commenti documento non disponibili.');
        return;
      }

      setNotes(result?.data?.notes || []);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [documentId, loadNotes, open]);

  async function submitNote(payload) {
    const body = String(payload?.body || '').trim();
    if (!body || isSaving || !documentId) return { ok: false };

    setIsSaving(true);
    setError('');
    const result = await saveNote(documentId, body);
    setIsSaving(false);

    if (result?.ok === false) {
      setError(result.message || 'Commento non salvato.');
      return result;
    }

    setNotes((current) => [...current, result.data.note]);
    return result;
  }

  async function submitReply(parentId, payload) {
    const body = String(payload?.body || '').trim();
    if (!body || !parentId || isSaving || !documentId) return { ok: false };

    setIsSaving(true);
    setError('');
    const result = await saveNote(documentId, body, parentId);
    setIsSaving(false);

    if (result?.ok === false) {
      setError(result.message || 'Risposta non salvata.');
      return result;
    }

    setNotes((current) => [...current, result.data.note]);
    return result;
  }

  async function deleteNote(noteId) {
    if (!noteId || deletingNoteId) return;

    setDeletingNoteId(noteId);
    setError('');
    const result = await removeNote(documentId, noteId);
    setDeletingNoteId('');

    if (!result?.ok) {
      setError(result.message || 'Commento non eliminato.');
      return;
    }

    setNotes((current) => current.filter((note) => String(note.id) !== String(noteId)));
  }

  return (
    <>
      <CommentThread
        key={documentId}
        autoFocus={autoFocus}
        title="Commenti"
        comments={notes}
        currentUser={currentUser}
        isLoading={isLoading}
        isSubmitting={isSaving}
        error={error}
        emptyLabel="Nessun commento presente per questo documento."
        placeholder="Aggiungi un commento al documento..."
        submitLabel="Aggiungi commento"
        onSubmitComment={submitNote}
        onSubmitReply={submitReply}
        onDeleteComment={(note) => setNoteDeleteRequest(note)}
        allowReplies
        disabled={!documentId || Boolean(deletingNoteId)}
      />
      <ConfirmActionDialog
        open={Boolean(noteDeleteRequest)}
        title="Eliminare questo commento documento?"
        description="Il commento verra rimosso definitivamente."
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setNoteDeleteRequest(null)}
        onConfirm={async () => {
          const noteId = noteDeleteRequest?.id;
          setNoteDeleteRequest(null);
          await deleteNote(noteId);
        }}
      />
    </>
  );
}
